import { useLocation } from 'react-router-dom'

import { AvailableModules, Features } from 'generated/featureDefinitions'
import { CreateUpsellRequestDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

export enum ValidReasons {
  MissingFeatureLegacy = 'Requesting feature not available in current plan (legacy plan)',
  MissingAddon = 'Requesting a missing addon',
  MissingFeature = 'Requesting feature not available in current plan',
  UpgradeFromLegacy = 'Upgrade from legacy plan',
  Activation = 'Request product services activation',
}

export type CreateProps = {
  requestReason?: ValidReasons
  moduleRequested?: AvailableModules | 'benefits' | 'datev'
  featureRequested?: Features
  requestSource?: string
  requestSourceCategory?: string
  requestSourceComponent?: string
  meetingBooked?: boolean
  pageSource?: string
  variant?: 'A' | 'B' | 'revamp' | null
  moreInfo?: string
  upsellRequestAmount?: number
  upsellRequestStatus?: 'Close won' | 'Opportunity detected' | 'Meeting booked completed'
  upsellRequestComponentType?: string
  upsellRequestType?: string
  owner?: string
  hubspotModuleName?: string
}

const useCreateUpsellRequest = () => {
  const {
    company: { subscription: currentSubscription },
  } = useCurrentSession()
  const { pathname } = useLocation()
  const [createUpsell, { loading, called }] = useMutation(CreateUpsellRequestDocument, {
    extractorFn: (data) => data.upsellMutations.createUpsellUpsellRequest,
  })

  return {
    loading,
    called,
    create({
      requestSource,
      requestSourceCategory,
      requestSourceComponent,
      moduleRequested,
      featureRequested,
      requestReason,
      meetingBooked,
      pageSource,
      variant = 'A',
      moreInfo,
      upsellRequestAmount,
      upsellRequestStatus,
      upsellRequestComponentType,
      upsellRequestType,
      owner,
      hubspotModuleName,
    }: CreateProps) {
      if (!currentSubscription.inFreeTrial) {
        track(trackingEvents.upsell.requestUpsell, {
          module_requested: moduleRequested,
          feature_request: featureRequested,
          request_source: requestSource,
          request_reason: requestReason,
          page_path: pathname,
          is_renamed: true,
          variant,
          page_source: pageSource,
        })
      }

      return createUpsell({
        variables: {
          featureRequested,
          moduleRequested: moduleRequested?.split(',')[0],
          requestSource,
          requestSourceCategory,
          requestSourceComponent,
          requestReason,
          meetingBooked,
          moreInfo,
          upsellRequestAmount,
          upsellRequestStatus,
          upsellRequestComponentType,
          upsellRequestType,
          owner,
          hubspotModuleName,
        },
      })
    },
  }
}

export default useCreateUpsellRequest
